import React, { useState, useEffect, useCallback } from 'react';
import isNil from 'lodash/isNil';
import noop from 'lodash/noop';
import OnMountAndUnmount from '@atlassian/jira-common-util-op-triggers/src/on-mount-and-unmount/OpTrigger.tsx';
import {
	useActiveRightSidebarState,
	useRightSidebarController,
} from '@atlassian/jira-layout-controller/src/controllers/layout-controller/consumers/right-sidebar-controller/index.tsx';
import { useContainerContext } from '@atlassian/jira-providers-container-context/src/main.tsx';
import { useProjectContext } from '@atlassian/jira-providers-project-context/src/index.tsx';
import { RIGHT_SIDE_PANEL_ID as FIRST_IMPRESSIONS_PANEL_ID } from '@atlassian/jira-servicedesk-first-impressions/src/common/constants.tsx';
import { GETTING_STARTED_PANEL_ID } from '@atlassian/jira-servicedesk-getting-started-panel-common/src/constants.tsx';
import {
	JsdGettingStartedPanelOpenButton,
	JsdGettingStartedPanelReopenSpotlight,
} from '@atlassian/jira-servicedesk-getting-started-panel/src/async.tsx';
import type { ProjectId } from '@atlassian/jira-shared-types/src/general.tsx';
import { useQuickstart } from '@atlassian/jira-software-onboarding-quickstart/src/controllers/should-show-experience/index.tsx';
import { JswQuickStartShouldRender as JsmQuickStartShouldRender } from '@atlassian/jira-software-onboarding-quickstart/src/JswQuickStartShouldRender.tsx';
import { JswOnboardingButton as JsmOnboardingButton } from '@atlassian/jira-software-onboarding-quickstart/src/ui/async.tsx';

const autoClosePanelIds = [GETTING_STARTED_PANEL_ID, FIRST_IMPRESSIONS_PANEL_ID];

const CloseSidePanelOnProjectIdChange = ({
	projectId,
	activePanelId,
}: {
	projectId: ProjectId;
	activePanelId: string;
}) => {
	const { closeCurrentRightSidebar } = useRightSidebarController();
	const [currentProjectId, setCurrentProjectId] = useState(projectId);

	const closeSidebarIfElligible = useCallback(() => {
		if (autoClosePanelIds.includes(activePanelId)) {
			closeCurrentRightSidebar();
		}
	}, [activePanelId, closeCurrentRightSidebar]);

	useEffect(() => {
		if (projectId !== currentProjectId) {
			setCurrentProjectId(projectId);
			closeSidebarIfElligible();
		}
	}, [closeSidebarIfElligible, currentProjectId, projectId]);

	return <OnMountAndUnmount mount={noop} unmount={closeSidebarIfElligible} />;
};

// This component ensures that right sidebars are closed when switching between projects.
// Without it, if a panel is open in the old project, the new project will be unable
// to open a different panel.
//
// For example, when switching from an ESM demo project to an ITSM project, if the
// FIC (First Impressions Checklist) is open in the demo project, the Getting Started Panel
// is unable to show in the ITSM project, as the sidebar state maintains that the FIC
// is still open.
const SidePanelAutoCloser = () => {
	const [{ data: containerContext }] = useContainerContext();
	const projectId =
		containerContext?.contextType === 'project' && !isNil(containerContext.project)
			? containerContext.project.id
			: undefined;

	const activeRightSidebarState = useActiveRightSidebarState();
	const activePanelId = activeRightSidebarState?.panelId;
	return projectId !== undefined && activePanelId !== undefined ? (
		<CloseSidePanelOnProjectIdChange projectId={projectId} activePanelId={activePanelId} />
	) : null;
};

export const Onboarding = () => {
	const projectContext = useProjectContext();
	const projectContextData = projectContext?.data;

	const canShowExperience = useQuickstart(projectContextData);

	return canShowExperience ? (
		<>
			<JsmQuickStartShouldRender />
			<JsmOnboardingButton />
		</>
	) : (
		<>
			<SidePanelAutoCloser />
			<JsdGettingStartedPanelOpenButton />
			<JsdGettingStartedPanelReopenSpotlight />
		</>
	);
};

export default Onboarding;
